import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import FramedAvatar from '../components/FramedAvatar';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  ArrowLeft,
  MapPin,
  Users,
  Search,
  Crown,
  Anchor,
  Ship,
  X
} from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icon in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// French cities with coordinates
const FRENCH_CITIES_COORDS = {
  'paris': [48.8566, 2.3522],
  'marseille': [43.2965, 5.3698],
  'lyon': [45.7640, 4.8357],
  'toulouse': [43.6047, 1.4442],
  'nice': [43.7102, 7.2620],
  'nantes': [47.2184, -1.5536],
  'strasbourg': [48.5734, 7.7521],
  'montpellier': [43.6108, 3.8767],
  'bordeaux': [44.8378, -0.5792],
  'lille': [50.6292, 3.0573],
  'rennes': [48.1173, -1.6778],
  'reims': [49.2583, 4.0317],
  'le havre': [49.4944, 0.1079],
  'le mans': [48.0061, 0.1996],
  'toulon': [43.1242, 5.9280],
  'grenoble': [45.1885, 5.7245],
  'dijon': [47.3220, 5.0415],
  'angers': [47.4784, -0.5632],
  'nîmes': [43.8367, 4.3601],
  'clermont-ferrand': [45.7772, 3.0870],
  'aix-en-provence': [43.5297, 5.4474],
  'brest': [48.3904, -4.4861],
  'tours': [47.3941, 0.6848],
  'amiens': [49.8941, 2.2958],
  'limoges': [45.8336, 1.2611],
  'perpignan': [42.6986, 2.8954],
  'metz': [49.1193, 6.1757],
  'besançon': [47.2378, 6.0241],
  'orléans': [47.9029, 1.9039],
  'rouen': [49.4432, 1.0999],
  'mulhouse': [47.7508, 7.3359],
  'caen': [49.1829, -0.3707],
  'nancy': [48.6921, 6.1844],
  'avignon': [43.9493, 4.8055],
  'dunkerque': [51.0343, 2.3768],
  'poitiers': [46.5802, 0.3404],
  'pau': [43.2951, -0.3708],
  'la rochelle': [46.1603, -1.1511],
  'cannes': [43.5528, 7.0174],
  'antibes': [43.5808, 7.1239],
  'calais': [50.9513, 1.8587],
  'colmar': [48.0796, 7.3558],
  'chambéry': [45.5646, 5.9178],
  'annecy': [45.8992, 6.1294],
  'saint-malo': [48.6493, -2.0007],
  'vannes': [47.6586, -2.7600],
  'quimper': [47.9960, -4.1024],
  'lorient': [47.7482, -3.3702],
  'gaillac': [43.9014, 1.8972],
  'albi': [43.9298, 2.1480],
  'castres': [43.6060, 2.2394],
  'rodez': [44.3497, 2.5752],
  'cahors': [44.4475, 1.4403],
  'montauban': [44.0176, 1.3549],
  'tarbes': [43.2328, 0.0784],
  'auch': [43.6463, 0.5855],
  'agen': [44.2033, 0.6166],
  'périgueux': [45.1840, 0.7180],
  'bergerac': [44.8530, 0.4860],
  'brive': [45.1598, 1.5334],
  'tulle': [45.2667, 1.7667],
  'aurillac': [44.9306, 2.4397],
  'le puy': [45.0428, 3.8845],
  'saint-etienne': [45.4397, 4.3872],
  'roanne': [46.0343, 4.0689],
  'vichy': [46.1267, 3.4250],
  'moulins': [46.5667, 3.3333],
  'valence': [44.9334, 4.8924],
  'ajaccio': [41.9192, 8.7386],
  'bastia': [42.7029, 9.4507]
};

// Custom marker icon based on member count
const createClusterIcon = (count) => {
  const size = count > 5 ? 40 : count > 2 ? 35 : 30;
  return L.divIcon({
    html: `<div style="background: linear-gradient(135deg, #06b6d4 0%, #3b82f6 100%); color: white; width: ${size}px; height: ${size}px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-weight: bold; font-size: 14px; box-shadow: 0 2px 8px rgba(0,0,0,0.3); border: 2px solid white;">${count}</div>`,
    className: 'custom-cluster-icon',
    iconSize: [size, size],
    iconAnchor: [size/2, size/2]
  });
};

// Gold marker for anonymous investments
const createInvestmentIcon = (count) => {
  const size = count > 3 ? 34 : 28;
  return L.divIcon({
    html: `<div style="background: linear-gradient(135deg, #f59e0b 0%, #b45309 100%); color: white; width: ${size}px; height: ${size}px; border-radius: 6px; display: flex; align-items: center; justify-content: center; font-weight: bold; font-size: 13px; box-shadow: 0 2px 8px rgba(0,0,0,0.35); border: 2px solid white; transform: rotate(45deg);"><span style="transform: rotate(-45deg);">${count}</span></div>`,
    className: 'custom-inv-icon',
    iconSize: [size, size],
    iconAnchor: [size/2, size/2]
  });
};

// Component to recenter map
const RecenterMap = ({ coords }) => {
  const map = useMap();
  useEffect(() => {
    if (coords) {
      map.flyTo(coords, 10, { duration: 1 });
    }
  }, [coords, map]);
  return null;
};

const MembersMapPage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  
  const [members, setMembers] = useState([]);
  const [investments, setInvestments] = useState([]);
  const [communityFeed, setCommunityFeed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCity, setSelectedCity] = useState(null);
  const [recenterCoords, setRecenterCoords] = useState(null);
  const [geocodeCache, setGeocodeCache] = useState({});
  const [geocoding, setGeocoding] = useState(false);

  useEffect(() => {
    fetchMembers();
    fetchInvestments();
    fetchCommunityFeed();
    // Load cached geocodes from localStorage
    const cached = localStorage.getItem('odys_geocode_cache');
    if (cached) {
      try {
        setGeocodeCache(JSON.parse(cached));
      } catch (e) {}
    }
  }, []);

  const fetchInvestments = async () => {
    try {
      const r = await api.get('/members/investments/map');
      setInvestments(r.data?.pins || []);
    } catch (e) {}
  };

  const fetchCommunityFeed = async () => {
    try {
      const r = await api.get('/members/community-feed?limit=15');
      setCommunityFeed(r.data?.events || []);
    } catch (e) {}
  };

  // Geocode a city using Nominatim API
  const geocodeCity = async (cityName) => {
    const normalizedCity = cityName.toLowerCase().trim();
    
    // Check predefined coords first
    if (FRENCH_CITIES_COORDS[normalizedCity]) {
      return FRENCH_CITIES_COORDS[normalizedCity];
    }
    
    // Check cache
    if (geocodeCache[normalizedCity]) {
      return geocodeCache[normalizedCity];
    }
    
    // Geocode via Nominatim
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(cityName)},France&limit=1`,
        { headers: { 'User-Agent': 'OdysInvest Community App' } }
      );
      const data = await response.json();
      
      if (data && data.length > 0) {
        const coords = [parseFloat(data[0].lat), parseFloat(data[0].lon)];
        // Update cache
        const newCache = { ...geocodeCache, [normalizedCity]: coords };
        setGeocodeCache(newCache);
        localStorage.setItem('odys_geocode_cache', JSON.stringify(newCache));
        return coords;
      }
    } catch (error) {
      console.error('Geocode error for', cityName, error);
    }
    
    return null;
  };

  const fetchMembers = async () => {
    try {
      const response = await api.get('/members/map');
      setMembers(response.data);
    } catch (error) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const getInitials = (firstName, lastName) => {
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase() || '?';
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin':
        return <Crown className="w-3 h-3 text-amber-500" />;
      case 'major':
        return <Ship className="w-3 h-3 text-blue-500" />;
      default:
        return <Anchor className="w-3 h-3 text-primary" />;
    }
  };

  // Group members by city
  const membersByCity = useMemo(() => {
    return members.reduce((acc, member) => {
      const city = member.city.toLowerCase().trim();
      if (!acc[city]) {
        acc[city] = [];
      }
      acc[city].push(member);
      return acc;
    }, {});
  }, [members]);

  // Group investments by city (anonymous)
  const investmentsByCity = useMemo(() => {
    return (investments || []).reduce((acc, inv) => {
      const city = (inv.city || '').toLowerCase().trim();
      if (!city) return acc;
      if (!acc[city]) acc[city] = [];
      acc[city].push(inv);
      return acc;
    }, {});
  }, [investments]);

  // Get cities with coordinates (geocode missing ones)
  const [citiesWithCoords, setCitiesWithCoords] = useState([]);
  const [investmentCitiesWithCoords, setInvestmentCitiesWithCoords] = useState([]);
  
  useEffect(() => {
    const geocodeAllCities = async () => {
      if (Object.keys(membersByCity).length === 0) return;
      
      setGeocoding(true);
      const results = [];
      
      for (const [city, cityMembers] of Object.entries(membersByCity)) {
        const normalizedCity = city.toLowerCase().trim();
        let coords = FRENCH_CITIES_COORDS[normalizedCity] || geocodeCache[normalizedCity];
        
        if (!coords) {
          // Geocode this city
          coords = await geocodeCity(city);
          // Add small delay to respect Nominatim rate limits
          await new Promise(r => setTimeout(r, 200));
        }
        
        if (coords) {
          results.push({
            name: city,
            members: cityMembers,
            coords: coords
          });
        }
      }
      
      setCitiesWithCoords(results);
      setGeocoding(false);
    };
    
    geocodeAllCities();
  }, [membersByCity, geocodeCache]);

  // Geocode investment cities (offset slightly from member markers to avoid overlap)
  useEffect(() => {
    const run = async () => {
      if (Object.keys(investmentsByCity).length === 0) {
        setInvestmentCitiesWithCoords([]);
        return;
      }
      const results = [];
      for (const [city, invs] of Object.entries(investmentsByCity)) {
        const normalizedCity = city.toLowerCase().trim();
        let coords = FRENCH_CITIES_COORDS[normalizedCity] || geocodeCache[normalizedCity];
        if (!coords) {
          coords = await geocodeCity(city);
          await new Promise(r => setTimeout(r, 200));
        }
        if (coords) {
          // Offset so investment pins don't sit exactly on member pins
          const offset = [coords[0] + 0.015, coords[1] + 0.015];
          results.push({ name: city, investments: invs, coords: offset });
        }
      }
      setInvestmentCitiesWithCoords(results);
    };
    run();
  }, [investmentsByCity, geocodeCache]);

  const filteredCities = useMemo(() => {
    if (!searchQuery) return citiesWithCoords;
    return citiesWithCoords.filter(c => 
      c.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [citiesWithCoords, searchQuery]);

  const handleCityClick = (city) => {
    setSelectedCity(city.name === selectedCity ? null : city.name);
    if (city.coords && city.name !== selectedCity) {
      setRecenterCoords(city.coords);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <MapPin className="w-12 h-12 text-primary animate-pulse mx-auto mb-4" />
          <p className="text-muted-foreground">Chargement de la carte...</p>
        </div>
      </div>
    );
  }

  if (geocoding) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <MapPin className="w-12 h-12 text-primary animate-bounce mx-auto mb-4" />
          <p className="text-muted-foreground">Géolocalisation des villes...</p>
          <p className="text-xs text-muted-foreground mt-2">{citiesWithCoords.length} villes localisées</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="members-map-page">
      <div className="noise-overlay" />

      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-xl sticky top-0 z-[1000]">
        <div className="max-w-7xl mx-auto px-4 py-3 sm:py-4 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0">
            <Button variant="ghost" size="icon" onClick={() => navigate('/')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center shrink-0">
                <MapPin className="w-5 h-5 text-blue-500" />
              </div>
              <div className="min-w-0">
                <h1 className="font-heading font-bold text-base sm:text-xl truncate">Carte des Membres</h1>
                <p className="text-xs sm:text-sm text-muted-foreground truncate">
                  {members.length} membre{members.length !== 1 ? 's' : ''} • {citiesWithCoords.length} ville{citiesWithCoords.length !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
          </div>
          
          <div className="relative w-36 sm:w-64 shrink-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Rechercher une ville..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-secondary/50"
            />
          </div>
        </div>
      </header>

      {/* Main Content — carte en haut puis liste des villes sur mobile, côte à côte sur ordinateur */}
      <div className="flex flex-col-reverse md:flex-row h-[calc(100dvh-125px)] md:h-[calc(100vh-73px)] overflow-hidden">
        {/* Sidebar - City List */}
        <aside className="w-full md:w-80 flex-1 md:flex-none min-h-0 border-t md:border-t-0 md:border-r border-border bg-card/30 overflow-y-auto">
          <div className="p-4 space-y-2">
            {filteredCities.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">Aucune ville trouvée</p>
            ) : (
              filteredCities.map((city) => (
                <Card 
                  key={city.name}
                  className={`cursor-pointer transition-all hover:border-primary/50 ${selectedCity === city.name ? 'border-primary bg-primary/5' : ''}`}
                  onClick={() => handleCityClick(city)}
                >
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-primary" />
                        <span className="font-medium capitalize">{city.name}</span>
                      </div>
                      <Badge variant="secondary">{city.members.length}</Badge>
                    </div>
                    
                    {selectedCity === city.name && (
                      <div className="mt-3 space-y-2 border-t border-border pt-3">
                        {city.members.map((member) => (
                          <div key={member.id} className="flex items-center gap-2">
                            <FramedAvatar
                              src={member.avatar_url}
                              fallback={getInitials(member.first_name, member.last_name)}
                              frame={member.profile_frame}
                              role={member.role}
                              seniority={member.seniority}
                              size="md"
                            />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">
                                {member.first_name} {member.last_name}
                              </p>
                              <div className="flex items-center gap-1">
                                {getRoleIcon(member.role)}
                                <span className="text-xs text-muted-foreground">Niv.{member.level || 1}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </aside>

        {/* Map */}
        <div className="relative h-[52%] shrink-0 md:h-auto md:flex-1 md:shrink">
          <MapContainer
            center={[46.603354, 1.888334]} // Center of France
            zoom={6}
            style={{ height: '100%', width: '100%' }}
            className="z-0"
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            
            <RecenterMap coords={recenterCoords} />
            
            {filteredCities.map((city) => (
              <Marker 
                key={city.name}
                position={city.coords}
                icon={createClusterIcon(city.members.length)}
                eventHandlers={{
                  click: () => handleCityClick(city)
                }}
              >
                <Popup>
                  <div className="p-2 min-w-[200px]">
                    <h3 className="font-bold text-lg capitalize flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-blue-500" />
                      {city.name}
                    </h3>
                    <p className="text-sm text-gray-500 mb-3">{city.members.length} membre{city.members.length > 1 ? 's' : ''}</p>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {city.members.map((member) => (
                        <div
                          key={member.id}
                          className="flex items-center gap-2 p-1 rounded hover:bg-gray-100 cursor-pointer"
                          onClick={() => navigate(`/members/${member.id}`)}
                          data-testid={`map-member-${member.id}`}
                        >
                          <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-xs font-bold text-blue-600">
                            {getInitials(member.first_name, member.last_name)}
                          </div>
                          <div>
                            <p className="text-sm font-medium">{member.first_name} {member.last_name}</p>
                            <p className="text-xs text-gray-400">Niveau {member.level || 1} · Voir profil</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </Popup>
              </Marker>
            ))}

            {/* Anonymous investment pins */}
            {investmentCitiesWithCoords.map((c) => (
              <Marker
                key={`inv-${c.name}`}
                position={c.coords}
                icon={createInvestmentIcon(c.investments.length)}
              >
                <Popup>
                  <div className="p-2 min-w-[220px]">
                    <h3 className="font-bold text-base capitalize flex items-center gap-2">
                      <span className="inline-block w-3 h-3 rounded-sm bg-amber-500" />
                      {c.name}
                    </h3>
                    <p className="text-xs text-gray-500 mb-3">
                      {c.investments.length} investissement{c.investments.length > 1 ? 's' : ''} de la communauté
                    </p>
                    <div className="space-y-1 max-h-40 overflow-y-auto">
                      {c.investments.map((inv) => (
                        <div key={inv.id} className="text-xs flex items-center justify-between gap-2 py-1 border-b last:border-0">
                          <span className="capitalize">
                            {(inv.type || '').replace(/_/g, ' ')}
                          </span>
                          <span className="text-gray-400 capitalize">
                            {(inv.status || '').replace(/_/g, ' ')}
                          </span>
                        </div>
                      ))}
                    </div>
                    <p className="text-[10px] text-gray-400 mt-2 italic">Propriétaires anonymes</p>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>

          {/* Legend */}
          <div className="absolute bottom-4 left-4 bg-card/95 backdrop-blur-sm rounded-lg p-3 border border-border z-[500] space-y-1">
            <p className="text-xs font-medium mb-1">Légende</p>
            <div className="flex items-center gap-2 text-xs">
              <div className="w-6 h-6 rounded-full bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center text-white text-[10px] font-bold">3</div>
              <span className="text-muted-foreground">Membres domiciliés</span>
            </div>
            <div className="flex items-center gap-2 text-xs">
              <div className="w-5 h-5 bg-gradient-to-br from-amber-500 to-amber-700 flex items-center justify-center text-white text-[10px] font-bold" style={{transform:'rotate(45deg)'}}><span style={{transform:'rotate(-45deg)'}}>2</span></div>
              <span className="text-muted-foreground">Investissements (anonymes)</span>
            </div>
          </div>

          {/* L'Équipage progresse — live community feed */}
          {communityFeed.length > 0 && (
            <div className="absolute top-4 right-4 w-80 max-w-[calc(100vw-2rem)] bg-card/95 backdrop-blur-sm rounded-lg border border-border z-[500] shadow-lg" data-testid="community-feed">
              <div className="p-3 border-b border-border">
                <p className="text-sm font-heading flex items-center gap-2">
                  <span className="inline-block w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                  L'Équipage progresse
                </p>
                <p className="text-[10px] text-muted-foreground">Actions récentes de la communauté</p>
              </div>
              <div className="p-2 max-h-72 overflow-y-auto space-y-1">
                {communityFeed.slice(0, 12).map((e, i) => (
                  <div
                    key={`feed-${i}`}
                    className="text-xs p-2 rounded hover:bg-secondary/40 cursor-pointer transition-colors"
                    onClick={() => e.url && navigate(e.url)}
                  >
                    <p className="text-foreground">
                      <span className="font-medium">{e.user?.first_name}</span>{' '}
                      <span className="text-muted-foreground">{e.label}</span>
                    </p>
                    {e.detail && (
                      <p className="text-[11px] text-muted-foreground truncate mt-0.5">{e.detail}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MembersMapPage;
